import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Input, Radio, Select, Switch, Typography } from 'antd';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import ContextBar from '../components/common/ContextBar';
import { DataState, Skeleton, deriveDataState } from '../components/loading';
import { isTokenValid } from '../utils';
import { apiErrorMessage } from '../utils/apiErrors';
import { formatDisplayDate } from '../utils/dateDisplay';
import { normalizeId } from '../utils/ids';
import { nestSubCollections, nestedOptionClass } from '../utils/collectionFamily';
import useDateFormat from '../hooks/useDateFormat';
import {
	ACCESS_OPTIONS,
	ACTS_AS_YOU,
	CARDS_CONSEQUENCE,
	DELETE_CONSEQUENCE,
	MAX_CONNECTION_NAME,
	authorizeClient,
	buildRedirect,
	describeClient,
	parseScopeParam,
	scopesFor,
} from '../services/oauth';
import { useCollections } from '../contexts/CollectionsContext';

const { Text } = Typography;

// The error sentences this page shows. Quoted in the user guide
// (docs/guides/assistants/connect-claude-web-or-chatgpt.md) — change both.
export const INCOMPLETE_REQUEST = 'This connection request is incomplete. Start again from the assistant.';
export const UNVERIFIED_CLIENT = 'This connection request could not be verified.';
export const ALLOW_FAILED = 'Could not connect the assistant.';

// The Connection choice's value for "make another one".
const NEW_CONNECTION = 'new';

/**
 * /oauth/authorize — the OAuth consent screen, and deliberately the
 * key-creation form: which collections, read or read-and-write, Can change
 * cards, Can delete records (off unless the person turns it on). An assistant (Claude on the web, ChatGPT, Claude Code) sends the
 * person here; a person who is not signed in goes through the ordinary
 * login — MFA included — and comes back to this same URL.
 *
 * The same assistant can be connected more than once — two Claude accounts
 * present the same client to keepr, so keepr cannot tell a second account
 * from the same one reconnecting. When the person already has a connection
 * from this assistant, the page asks: add a new connection (the default), or
 * replace one of theirs. Either way the connection gets a name.
 *
 * Two things this page never does. It never redirects on a request it
 * cannot verify: an unknown client or an unregistered redirect URI is an
 * error PAGE, because a redirect to an unverified address is exactly the
 * open redirector OAuth warns about. And it never lets the API redirect:
 * Allow asks the API for a code and the page itself sends the browser to
 * the client, so the API never 302s to a third-party URL from an XHR.
 */
export default function OAuthAuthorize() {
	const { message } = App.useApp();
	useDateFormat();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const request = useMemo(() => ({
		clientId: searchParams.get('client_id') || '',
		redirectUri: searchParams.get('redirect_uri') || '',
		state: searchParams.get('state') || '',
		codeChallenge: searchParams.get('code_challenge') || '',
		codeChallengeMethod: searchParams.get('code_challenge_method') || '',
		resource: searchParams.get('resource') || '',
		scope: searchParams.get('scope') || '',
	}), [searchParams]);

	const incomplete = !request.clientId || !request.redirectUri || !request.codeChallenge || request.codeChallengeMethod !== 'S256';

	// null = still asking; { error } = refused; otherwise the client.
	const [client, setClient] = useState(null);
	// The client could not be asked (no network, a server fault): unlike a
	// refusal, this is worth another try, and says so with Retry.
	const [clientUnreachable, setClientUnreachable] = useState(false);
	const [clientKey, setClientKey] = useState(0);
	const requested = useMemo(() => parseScopeParam(request.scope), [request.scope]);
	const [access, setAccess] = useState(requested.access);
	const [cards, setCards] = useState(requested.cards);
	// Can delete records starts OFF even when the client's `scope` asks for
	// `delete`: access and cards take the request as a hint, but removing
	// things is the one authority a person must switch on themselves. It is
	// only live when it could ride on write or cards (the API refuses it
	// alone), and reads off otherwise.
	const [canDelete, setCanDelete] = useState(false);
	const deleteAllowed = access === 'write' || cards;
	const [collectionMode, setCollectionMode] = useState('all');
	const [collectionIds, setCollectionIds] = useState([]);
	const [allowing, setAllowing] = useState(false);
	// NEW_CONNECTION or the id of an existing connection to replace; null
	// when every slot is taken and the person has not chosen one yet.
	const [connection, setConnection] = useState(NEW_CONNECTION);
	const [name, setName] = useState('');
	// Once the person types a name, choosing another connection leaves it alone.
	const [nameTouched, setNameTouched] = useState(false);

	const signedIn = isTokenValid();

	// The shared collections list, asked again as the consent screen opens: the
	// person ticks which of THEIR collections the connection may reach.
	const { collections, available: collectionsLoaded, error: collectionsError, refresh: refreshCollections } = useCollections({ refreshOnOpen: signedIn && !incomplete });
	const collectionsStatus = collectionsLoaded ? 'ready' : (collectionsError ? 'error' : 'loading');

	useEffect(() => {
		if (!signedIn || incomplete) return undefined;
		let active = true;
		setClient(null);
		setClientUnreachable(false);
		describeClient({ clientId: request.clientId, redirectUri: request.redirectUri })
			.then(info => {
				if (!active) return;
				// With the client, in one update: a new connection unless every
				// slot is taken, named with the API's suggestion.
				const initial = initialConnection(info);
				setClient(info);
				setConnection(initial);
				setName(initial ? info.suggestedName || info.clientName || '' : '');
				setNameTouched(false);
			})
			.catch(error => {
				if (!active) return;
				// Only an answer from the API can say the client is unverifiable;
				// no answer, or a server fault, says nothing about the client.
				const status = error?.response?.status;
				if (!status || status >= 500) setClientUnreachable(true);
				else setClient({ error: apiErrorMessage(error, UNVERIFIED_CLIENT) });
			});
		return () => { active = false; };
	}, [signedIn, incomplete, request.clientId, request.redirectUri, clientKey]);

	const existing = useMemo(() => existingConnectionsOf(client), [client]);
	const full = isFull(client);

	const defaultNameFor = useCallback((choice) => {
		if (choice && choice !== NEW_CONNECTION) {
			const replaced = existing.find(row => row.id === choice);
			if (replaced) return replaced.name;
		}
		return client?.suggestedName || client?.clientName || '';
	}, [existing, client]);

	const handleConnectionChange = useCallback((choice) => {
		setConnection(choice);
		if (!nameTouched) setName(defaultNameFor(choice));
	}, [nameTouched, defaultNameFor]);

	// Alphabetical, each sub-collection indented beneath its parent.
	const collectionOptions = useMemo(() => nestSubCollections(collections
		.filter(collection => normalizeId(collection?._id))
		.sort((a, b) => (a?.name || '').localeCompare(b?.name || '')))
		.map(({ collection, depth }) => ({
			value: normalizeId(collection._id),
			label: collection.name || '',
			className: nestedOptionClass(depth),
		})), [collections]);

	const collectionsValid = collectionMode === 'all' || collectionIds.length > 0;
	const canAllow = !!client && !client.error && collectionsValid && connection !== null && !allowing;

	const handleAllow = useCallback(async () => {
		if (!canAllow) return;
		setAllowing(true);
		try {
			const { code, iss } = await authorizeClient({
				clientId: request.clientId,
				redirectUri: request.redirectUri,
				codeChallenge: request.codeChallenge,
				codeChallengeMethod: request.codeChallengeMethod,
				resource: request.resource || undefined,
				scopes: scopesFor({ access, cards, canDelete: canDelete && deleteAllowed }),
				collectionIds: collectionMode === 'some' ? collectionIds : undefined,
				name,
				replaceGrantId: connection === NEW_CONNECTION ? null : connection,
			});
			// The redirect is the page's, on the URI the API just verified.
			window.location.assign(buildRedirect(client.redirectUri, { code, state: request.state, iss: iss || client.issuer }));
		} catch (error) {
			message.error(apiErrorMessage(error, ALLOW_FAILED));
			setAllowing(false);
		}
	}, [canAllow, request, access, cards, canDelete, deleteAllowed, collectionMode, collectionIds, name, connection, client, message]);

	const handleCancel = useCallback(() => {
		if (!client || client.error) return;
		window.location.assign(buildRedirect(client.redirectUri, { error: 'access_denied', state: request.state, iss: client.issuer }));
	}, [client, request.state]);

	if (!signedIn) {
		// The ordinary login, which brings the person back to this exact
		// request (the api client's own 401 path uses the same two params).
		const returnPath = encodeURIComponent(`${location.pathname}${location.search}`);
		return <Navigate to={`/login?isRedirect=true&returnPath=${returnPath}`} replace />;
	}

	const title = client && !client.error ? `Connect ${client.clientName} to keepr` : 'Connect an assistant to keepr';

	let body;
	if (incomplete) {
		body = <Alert type="error" showIcon message={INCOMPLETE_REQUEST} />;
	} else if (client?.error) {
		body = <Alert type="error" showIcon message={client.error} />;
	} else {
		body = (
			<DataState
				state={deriveDataState({ loading: client === null && !clientUnreachable, error: clientUnreachable })}
				hasData={client !== null}
				skeleton={<Skeleton variant="form-field" count={4} />}
				error={clientUnreachable ? 'Check your connection and try again.' : null}
				onRetry={() => setClientKey(n => n + 1)}
				label="this connection request"
			>
			{client ? (
			<form
				className="oauth-consent__form"
				onSubmit={event => { event.preventDefault(); handleAllow(); }}
			>
				<p className="oauth-consent__lead">
					<strong>{client.clientName}</strong> is asking to use keepr as you. Choose what it may reach.
					{client.clientHost ? (
						<span className="oauth-consent__host"> Requested by {client.clientHost}.</span>
					) : null}
				</p>
				{client.loopback ? (
					<Alert
						type="warning"
						showIcon
						className="oauth-consent__notice"
						message={`This sends the connection to an app running on your computer (${client.redirectHost}).`}
					/>
				) : null}

				{existing.length ? (
					<div className="oauth-consent__field">
						<span className="oauth-consent__field-label" id="oauth-connection-label">Connection</span>
						<p className="oauth-consent__hint oauth-consent__hint--above">
							{existing.length === 1
								? `You already have a connection from ${client.clientName}.`
								: `You already have ${existing.length} connections from ${client.clientName}.`}
							{' '}For another {client.clientName} account, add a new one. If it is the same account connecting again, replace its connection.
						</p>
						<Radio.Group
							aria-labelledby="oauth-connection-label"
							name="oauth-connection"
							className="oauth-consent__choices"
							value={connection}
							onChange={event => handleConnectionChange(event.target.value)}
						>
							<Radio value={NEW_CONNECTION} disabled={full}>Add a new connection</Radio>
							{existing.map(row => (
								<Radio key={row.id} value={row.id}>
									Replace {row.name}
									<span className="oauth-consent__choice-meta">
										{connectionMeta(row)}
									</span>
								</Radio>
							))}
						</Radio.Group>
						{full ? (
							<p className="oauth-consent__hint">
								{`You have ${existing.length} connections from ${client.clientName}, the most there can be. Replace one, or disconnect one in Account → Connected assistants.`}
							</p>
						) : null}
					</div>
				) : null}

				<div className="oauth-consent__field">
					<label className="oauth-consent__field-label" htmlFor="oauth-connection-name">Name</label>
					<Input
						id="oauth-connection-name"
						value={name}
						maxLength={MAX_CONNECTION_NAME}
						placeholder={defaultNameFor(connection)}
						onChange={event => { setName(event.target.value); setNameTouched(true); }}
					/>
					<p className="oauth-consent__hint">How this connection is listed in Account → Connected assistants. You can rename it there.</p>
				</div>

				<div className="oauth-consent__field">
					<span className="oauth-consent__field-label" id="oauth-collections-label">Collections</span>
					<Radio.Group
						aria-labelledby="oauth-collections-label"
						value={collectionMode}
						onChange={event => setCollectionMode(event.target.value)}
					>
						<Radio value="all">All collections I can access</Radio>
						<Radio value="some">Only these collections</Radio>
					</Radio.Group>
					{collectionMode === 'some' ? (
						<DataState
							className="mt-2"
							state={deriveDataState({ loading: collectionsStatus === 'loading', error: collectionsStatus === 'error' })}
							skeleton={<Skeleton variant="line" />}
							error={collectionsStatus === 'error' ? 'Check your connection and try again.' : null}
							onRetry={() => refreshCollections()}
							label="your collections"
						>
							<Select
								mode="multiple"
								id="oauth-collections"
								aria-label="Collections"
								className="oauth-consent__select"
								placeholder="Pick collections"
								value={collectionIds}
								options={collectionOptions}
								optionFilterProp="label"
								onChange={setCollectionIds}
							/>
						</DataState>
					) : null}
				</div>

				<div className="oauth-consent__field">
					<span className="oauth-consent__field-label" id="oauth-access-label">Access</span>
					<Radio.Group
						aria-labelledby="oauth-access-label"
						value={access}
						onChange={event => setAccess(event.target.value)}
					>
						{ACCESS_OPTIONS.map(option => (
							<Radio key={option.value} value={option.value}>{option.label}</Radio>
						))}
					</Radio.Group>
				</div>

				<div className="oauth-consent__field">
					<label className="oauth-consent__toggle">
						<Switch size="small" checked={cards} onChange={setCards} aria-label="Can change cards" />
						<span className="oauth-consent__field-label oauth-consent__field-label--inline">Can change cards</span>
					</label>
					<p className="oauth-consent__hint">{CARDS_CONSEQUENCE}</p>
				</div>

				<div className="oauth-consent__field">
					<label className="oauth-consent__toggle">
						<Switch
							size="small"
							checked={canDelete && deleteAllowed}
							disabled={!deleteAllowed}
							onChange={setCanDelete}
							aria-label="Can delete records"
						/>
						<span className="oauth-consent__field-label oauth-consent__field-label--inline">Can delete records</span>
					</label>
					<p className="oauth-consent__hint">
						{deleteAllowed ? DELETE_CONSEQUENCE : 'Needs Read and write or Can change cards: it can only delete what it can change.'}
					</p>
				</div>

				<Text type="secondary" className="oauth-consent__acts">{ACTS_AS_YOU}</Text>

				<div className="oauth-consent__actions">
					<Button onClick={handleCancel} disabled={allowing}>Cancel</Button>
					<Button type="primary" htmlType="submit" loading={allowing} disabled={!canAllow}>Allow</Button>
				</div>
			</form>
			) : null}
			</DataState>
		);
	}

	return (
		<>
			{/* It names no record, so there is no chip: a plain title under
			    the one breadcrumb segment above it. */}
			<ContextBar
				breadcrumb={[{ label: 'Account', to: '/account' }]}
				title={title}
			/>
			<section className="oauth-consent">
				{body}
			</section>
		</>
	);
}

function existingConnectionsOf(client) {
	return client && !client.error && Array.isArray(client.existingConnections) ? client.existingConnections : [];
}

/** Every connection this person may hold from this assistant is taken. */
function isFull(client) {
	return existingConnectionsOf(client).length >= (client?.maxConnections || Infinity);
}

/** What the Connection choice starts on: a new connection, or nothing when there is no room for one. */
function initialConnection(client) {
	return isFull(client) ? null : NEW_CONNECTION;
}

/** "last used Oct 3, 2026", "never used", with "expired" when its refresh token has died. */
function connectionMeta(row) {
	const parts = [row.lastUsedAt ? `last used ${formatDisplayDate(row.lastUsedAt)}` : 'never used'];
	if (row.status === 'expired') parts.push('expired');
	return parts.join(' · ');
}
